import "./CompanyPurchases.css";
import { usePermissions } from "./PermissionContext";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import JournalAuditModal from "./components/JournalAuditModal";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};


const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "";

  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "es-CO"
  );
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

const emptyPurchase = () => ({
  supplier_id: "",
  invoice_number: "",
  purchase_date: formatDateInput(new Date()),
  due_date: "",
  notes: "",
});

const emptyPayment = () => ({
  payment_date: formatDateInput(new Date()),
  payment_method: "transfer",
  amount: "",
  reference: "",
  notes: "",
});

const createEmptyLot = (quantity = "") => ({
  lot_number: "",
  expiration_date: "",
  quantity,
});

const LOT_QUANTITY_TOLERANCE = 0.0005;

function CompanyPurchases() {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState(null);

  const [purchaseForm, setPurchaseForm] = useState(emptyPurchase);
  const [cart, setCart] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [selectedReceiptId, setSelectedReceiptId] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [savingPurchase, setSavingPurchase] = useState(false);

  const navigate = useNavigate();
  const { company } = useParams();
  const { can } = usePermissions();

  const canCreatePurchases = can("purchases.create");
  const isReceiptLinked = Boolean(selectedReceiptId);
  const canViewAccounting = can("accounting.view");
  const canViewAudit = can("accounting.audit");

  const [linkedJournalEntry, setLinkedJournalEntry] =
    useState(null);
  const [auditJournalEntry, setAuditJournalEntry] =
    useState(null);

  const [receiptDetail, setReceiptDetail] = useState(null);
  const [loadingReceiptDetail, setLoadingReceiptDetail] =
    useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [purchaseDetail, setPurchaseDetail] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [savingPayment, setSavingPayment] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadSuppliers = useCallback(async () => {
    const response = await axios.get(`${API_URL}/suppliers/me`, {
      headers: getHeaders(),
      params: {
        active_only: true,
      },
    });

    setSuppliers(response.data);
  }, []);

  const loadProducts = useCallback(async () => {
    const response = await axios.get(`${API_URL}/products/me`, {
      headers: getHeaders(),
    });

    setProducts(
      response.data.filter((product) => product.is_active)
    );
  }, []);

  const loadPendingReceipts = useCallback(async () => {
    if (!canCreatePurchases) {
      setPendingReceipts([]);
      return;
    }

    const response = await axios.get(
      `${API_URL}/purchase-orders/me/receipts/pending-invoice`,
      {
        headers: getHeaders(),
      }
    );

    setPendingReceipts(response.data);
  }, [canCreatePurchases]);

  const loadPurchases = useCallback(async () => {
    const params = {};

    if (search.trim()) params.search = search.trim();
    if (statusFilter) params.purchase_status = statusFilter;

    const response = await axios.get(`${API_URL}/purchases/me`, {
      headers: getHeaders(),
      params,
    });

    setPurchases(response.data);
  }, [search, statusFilter]);

  const loadSummary = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/purchases/me/summary`,
      {
        headers: getHeaders(),
      }
    );

    setSummary(response.data);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadSuppliers(),
        loadProducts(),
        loadPurchases(),
        loadSummary(),
        loadPendingReceipts()
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [
    loadSuppliers,
    loadProducts,
    loadPurchases,
    loadSummary,
    loadPendingReceipts
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePurchaseChange = (event) => {
    const { name, value } = event.target;

    setPurchaseForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleReceiptChange = (event) => {
    const receiptId = event.target.value;

    if (!receiptId) {
      resetPurchase();
      return;
    }

    const receipt = pendingReceipts.find(
      (item) => Number(item.id) === Number(receiptId)
    );

    if (!receipt) {
      toast.error("Recepción no encontrada.");
      return;
    }

    const receiptCart = receipt.items.map((receiptItem) => {
      const product = products.find(
        (item) =>
          Number(item.id) === Number(receiptItem.product_id)
      );

      if (!product) {
        return null;
      }

      return {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        quantity: Number(receiptItem.quantity),
        unit_cost: Number(receiptItem.unit_cost),
        discount_amount: 0,
        tax_amount: 0,
        lots: [],
      };
    });

    if (receiptCart.some((item) => !item)) {
      toast.error(
        "Uno o más productos de la recepción no están disponibles."
      );
      return;
    }

    setSelectedReceiptId(receiptId);
    setSelectedProductId("");

    setPurchaseForm({
      ...emptyPurchase(),
      supplier_id: String(receipt.supplier_id),
      notes: receipt.notes || "",
    });

    setCart(receiptCart);
  };

  const addProductToCart = () => {
    if (!selectedProductId) {
      toast.error("Selecciona un producto.");
      return;
    }

    const product = products.find(
      (item) => Number(item.id) === Number(selectedProductId)
    );

    if (!product) {
      toast.error("Producto no encontrado.");
      return;
    }

    const alreadyExists = cart.some(
      (item) => item.product_id === product.id
    );

    if (alreadyExists) {
      toast.error(
        "El producto ya está en la compra. Ajusta la cantidad en la tabla."
      );
      return;
    }

    setCart((current) => [
      ...current,
      {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        quantity: 1,
        unit_cost: Number(product.purchase_price || 0),
        discount_amount: 0,
        tax_amount: 0,
        lots: [createEmptyLot(1)],
      },
    ]);

    setSelectedProductId("");
  };

  const updateCartItem = (productId, field, value) => {
    setCart((current) =>
      current.map((item) => {
        if (item.product_id !== productId) return item;

        const numericValue = Number(value);

        const updatedItem = {
          ...item,
          [field]: numericValue,
        };

        // Si solo hay un lote, su cantidad acompaña automáticamente
        // la cantidad total del producto.
        if (
          field === "quantity" &&
          item.lots.length === 1
        ) {
          updatedItem.lots = [
            {
              ...item.lots[0],
              quantity: numericValue,
            },
          ];
        }

        return updatedItem;
      })
    );
  };

  const updateLot = (
    productId,
    lotIndex,
    field,
    value
  ) => {
    setCart((current) =>
      current.map((item) => {
        if (item.product_id !== productId) return item;

        return {
          ...item,
          lots: item.lots.map((lot, index) => {
            if (index !== lotIndex) return lot;

            return {
              ...lot,
              [field]:
                field === "quantity"
                  ? Number(value)
                  : value,
            };
          }),
        };
      })
    );
  };

  const addLotToCartItem = (productId) => {
    setCart((current) =>
      current.map((item) => {
        if (item.product_id !== productId) return item;

        return {
          ...item,
          lots: [
            ...item.lots,
            createEmptyLot(""),
          ],
        };
      })
    );
  };

  const removeLotFromCartItem = (
    productId,
    lotIndex
  ) => {
    setCart((current) =>
      current.map((item) => {
        if (item.product_id !== productId) return item;

        if (item.lots.length === 1) {
          toast.error(
            "Cada producto debe conservar al menos un lote."
          );
          return item;
        }

        return {
          ...item,
          lots: item.lots.filter(
            (_, index) => index !== lotIndex
          ),
        };
      })
    );
  };

  const removeCartItem = (productId) => {
    setCart((current) =>
      current.filter(
        (item) => item.product_id !== productId
      )
    );
  };

  const cartTotals = useMemo(() => {
    return cart.reduce(
      (totals, item) => {
        const quantity = Number(item.quantity || 0);
        const unitCost = Number(item.unit_cost || 0);
        const discount = Number(item.discount_amount || 0);
        const tax = Number(item.tax_amount || 0);

        const subtotal = quantity * unitCost;
        const lineTotal = subtotal - discount + tax;

        return {
          subtotal: totals.subtotal + subtotal,
          discount: totals.discount + discount,
          tax: totals.tax + tax,
          total: totals.total + lineTotal,
        };
      },
      {
        subtotal: 0,
        discount: 0,
        tax: 0,
        total: 0,
      }
    );
  }, [cart]);

  const resetPurchase = () => {
    setPurchaseForm(emptyPurchase());
    setCart([]);
    setSelectedProductId("");
    setSelectedReceiptId("");
  };

  const handlePurchaseSubmit = async (event) => {
    event.preventDefault();
    if (!canCreatePurchases) {
      toast.error(
        "No tienes permiso para registrar compras."
      );
      return;
    }

    if (!purchaseForm.supplier_id) {
      toast.error("Selecciona un proveedor.");
      return;
    }

    if (cart.length === 0) {
      toast.error("Agrega al menos un producto a la compra.");
      return;
    }

    const invalidItem = cart.find(
      (item) =>
        Number(item.quantity) <= 0 ||
        Number(item.unit_cost) < 0 ||
        Number(item.discount_amount) < 0 ||
        Number(item.tax_amount) < 0
    );
    if (!isReceiptLinked) {
      for (const item of cart) {
        const lots = item.lots || [];

        if (lots.length === 0) {
          toast.error(
            `Agrega al menos un lote para "${item.name}".`
          );
          return;
        }

        const invalidLot = lots.find(
          (lot) => Number(lot.quantity) <= 0
        );

        if (invalidLot) {
          toast.error(
            `Cada lote de "${item.name}" debe tener una cantidad mayor que cero.`
          );
          return;
        }

        const lotQuantity = lots.reduce(
          (total, lot) =>
            total + Number(lot.quantity || 0),
          0
        );

        if (
          Math.abs(
            lotQuantity - Number(item.quantity || 0)
          ) > LOT_QUANTITY_TOLERANCE
        ) {
          toast.error(
            `Las cantidades de los lotes de "${item.name}" deben sumar ${item.quantity}.`
          );
          return;
        }

        const duplicateLotNumbers = lots
          .map((lot) =>
            lot.lot_number.trim().toUpperCase()
          )
          .filter(Boolean);

        if (
          new Set(duplicateLotNumbers).size !==
          duplicateLotNumbers.length
        ) {
          toast.error(
            `No repitas números de lote para "${item.name}".`
          );
          return;
        }

        const invalidExpiration = lots.find(
          (lot) =>
            lot.expiration_date &&
            lot.expiration_date < purchaseForm.purchase_date
        );

        if (invalidExpiration) {
          toast.error(
            `La fecha de vencimiento de "${item.name}" no puede ser anterior a la compra.`
          );
          return;
        }
      }
    }

    if (invalidItem) {
      toast.error(
        "Revisa cantidades, costos, descuentos e impuestos."
      );
      return;
    }

    setSavingPurchase(true);

    try {
      await axios.post(
        `${API_URL}/purchases/me`,
        {
          supplier_id: Number(purchaseForm.supplier_id),
          purchase_order_receipt_id: selectedReceiptId
            ? Number(selectedReceiptId)
            : null,
          invoice_number: purchaseForm.invoice_number || null,
          purchase_date: purchaseForm.purchase_date,
          due_date: purchaseForm.due_date || null,
          notes: purchaseForm.notes || null,
          items: cart.map((item) => ({
            product_id: item.product_id,
            quantity: Number(item.quantity),
            unit_cost: Number(item.unit_cost),
            discount_amount: Number(item.discount_amount || 0),
            tax_amount: Number(item.tax_amount || 0),
            lots: item.lots.map((lot) => ({
              lot_number: lot.lot_number.trim() || null,
              expiration_date: lot.expiration_date || null,
              quantity: Number(lot.quantity),
            })),
          })),
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        isReceiptLinked
          ? "Factura registrada y vinculada a la recepción. No se duplicó el inventario."
          : "Compra registrada. El inventario fue actualizado automáticamente."
      );

      resetPurchase();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingPurchase(false);
    }
  };

  const openPaymentModal = async (purchase) => {
    setSelectedPurchase(purchase);
    setPurchaseDetail(null);
    setPaymentForm({
      ...emptyPayment(),
      amount: Number(purchase.balance || 0),
    });
    setShowPaymentModal(true);
    setLoadingDetail(true);

    try {
      const response = await axios.get(
        `${API_URL}/purchases/me/${purchase.id}`,
        {
          headers: getHeaders(),
        }
      );

      setPurchaseDetail(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingDetail(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPurchase(null);
    setPurchaseDetail(null);
    setPaymentForm(emptyPayment());
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;

    setPaymentForm((current) => ({
      ...current,
      [name]: name === "amount" ? Number(value) : value,
    }));
  };

  const handlePaymentSubmit = async (event) => {
    event.preventDefault();
    if (!canCreatePurchases) {
      toast.error(
        "No tienes permiso para registrar pagos de compras."
      );
      return;
    }

    if (!selectedPurchase) return;

    const balance = Number(
      purchaseDetail?.balance ?? selectedPurchase.balance ?? 0
    );

    if (Number(paymentForm.amount) <= 0) {
      toast.error("El pago debe ser mayor que cero.");
      return;
    }

    if (Number(paymentForm.amount) > balance) {
      toast.error(
        "El pago no puede superar el saldo pendiente."
      );
      return;
    }

    setSavingPayment(true);

    try {
      const response = await axios.post(
        `${API_URL}/purchases/me/${selectedPurchase.id}/payments`,
        {
          ...paymentForm,
          amount: Number(paymentForm.amount),
        },
        {
          headers: getHeaders(),
        }
      );

      setPurchaseDetail(response.data);

      toast.success(
        paymentForm.payment_method === "cash"
          ? "Pago registrado y egreso enviado a Caja."
          : "Pago registrado correctamente."
      );

      await loadData();

      setPaymentForm({
        ...emptyPayment(),
        amount: Number(response.data.balance || 0),
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingPayment(false);
    }
  };

  const getStatusLabel = (purchaseStatus) => {
    const labels = {
      pending: "Pendiente",
      partial: "Pago parcial",
      paid: "Pagada",
      cancelled: "Anulada",
    };

    return labels[purchaseStatus] || purchaseStatus;
  };

  const handleViewPurchaseJournalEntry = async (purchase) => {
    try {
      const response = await axios.get(
        `${API_URL}/accounting/source-entry/purchase/${purchase.id}`,
        {
          headers: getHeaders(),
        }
      );

      setLinkedJournalEntry(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(
          "Esta compra todavía no tiene un comprobante contable asociado."
        );
        return;
      }

      toast.error(getErrorMessage(error));
    }
  };

  const openReceiptDetail = async (receiptId) => {
    setReceiptDetail(null);
    setLoadingReceiptDetail(true);

    try {
      const response = await axios.get(
        `${API_URL}/purchase-orders/me/receipts/${receiptId}`,
        {
          headers: getHeaders(),
        }
      );

      setReceiptDetail(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingReceiptDetail(false);
    }
  };

  return (
    <div className="company-purchases">
      <section className="purchases-heading">
        <div>
          <p className="purchases-kicker">Compras</p>
          <h1>Registro de compras</h1>
          <p>
            Registra compras, controla pagos y actualiza el inventario.
          </p>
        </div>

        <button
          type="button"
          className="btn-purchase-refresh"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      <section className="purchase-summary-grid">
        <article className="purchase-summary-card">
          <span>Compras registradas</span>
          <strong>{summary?.purchases_count ?? 0}</strong>
        </article>

        <article className="purchase-summary-card">
          <span>Total comprado</span>
          <strong>{money(summary?.total_purchases)}</strong>
        </article>

        <article className="purchase-summary-card pending">
          <span>Saldo pendiente</span>
          <strong>{money(summary?.pending_balance)}</strong>
        </article>

        <article className="purchase-summary-card overdue">
          <span>Cartera vencida</span>
          <strong>{money(summary?.overdue_balance)}</strong>
        </article>
      </section>

      <section className="purchases-card purchase-form-card">
        <div className="purchase-card-heading">
          <div>
            <h2>Nueva compra</h2>
            <p>
              Al guardar, los productos ingresarán automáticamente al inventario.
            </p>
          </div>

          {cart.length > 0 && (
            <button
              type="button"
              className="btn-purchase-clear"
              onClick={resetPurchase}
            >
              Limpiar compra
            </button>
          )}
        </div>

        {canCreatePurchases ? (
          <form onSubmit={handlePurchaseSubmit}>
            <div className="purchase-form-grid">
              <label>
                Origen de la compra
                <select
                  value={selectedReceiptId}
                  onChange={handleReceiptChange}
                >
                  <option value="">
                    Compra manual con ingreso de inventario
                  </option>

                  {pendingReceipts.map((receipt) => (
                    <option
                      key={receipt.id}
                      value={receipt.id}
                    >
                      {receipt.receipt_number} · Orden{" "}
                      {receipt.order_number} ·{" "}
                      {receipt.supplier_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Proveedor
                <select
                  name="supplier_id"
                  value={purchaseForm.supplier_id}
                  onChange={handlePurchaseChange}
                  disabled={isReceiptLinked}
                  required
                >
                  <option value="">Selecciona un proveedor</option>

                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name} — {supplier.nit}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Factura o referencia
                <input
                  type="text"
                  name="invoice_number"
                  value={purchaseForm.invoice_number}
                  onChange={handlePurchaseChange}
                  placeholder="Ejemplo: FC-000125"
                />
              </label>

              <label>
                Fecha de compra
                <input
                  type="date"
                  name="purchase_date"
                  value={purchaseForm.purchase_date}
                  onChange={handlePurchaseChange}
                  required
                />
              </label>

              <label>
                Fecha de vencimiento
                <input
                  type="date"
                  name="due_date"
                  value={purchaseForm.due_date}
                  onChange={handlePurchaseChange}
                />
              </label>
            </div>

            <label className="purchase-notes">
              Observaciones
              <textarea
                name="notes"
                value={purchaseForm.notes}
                onChange={handlePurchaseChange}
                placeholder="Información adicional sobre la compra"
                rows="2"
              />
            </label>

            {isReceiptLinked ? (
              <div className="purchase-readonly-message">
                Esta factura está vinculada a una recepción. Los productos,
                cantidades, costos, inventario y lotes provienen de esa
                recepción y no pueden modificarse.
              </div>
            ) : (
              <div className="purchase-product-picker">
                <select
                  value={selectedProductId}
                  onChange={(event) =>
                    setSelectedProductId(event.target.value)
                  }
                >
                  <option value="">
                    Selecciona un producto para agregar
                  </option>

                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.sku} — {product.name}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="btn-purchase-add-product"
                  onClick={addProductToCart}
                >
                  Agregar producto
                </button>
              </div>
            )}

            <div className="purchase-table-wrapper">
              <table className="purchase-cart-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Cantidad</th>
                    <th>Costo unitario</th>
                    <th>Descuento</th>
                    <th>Impuesto</th>
                    <th>Total</th>
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {cart.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="purchase-empty">
                        Agrega productos a esta compra.
                      </td>
                    </tr>
                  ) : (
                    cart.map((item) => {
                      const lineTotal =
                        Number(item.quantity) *
                        Number(item.unit_cost) -
                        Number(item.discount_amount || 0) +
                        Number(item.tax_amount || 0);

                      return (
                        <Fragment key={item.product_id}>
                          <tr>
                            <td>
                              <strong>{item.name}</strong>
                              <small>{item.sku}</small>
                            </td>

                            <td>
                              <input
                                type="number"
                                min="0.001"
                                step="0.001"
                                value={item.quantity}
                                disabled={isReceiptLinked}
                                onChange={(event) =>
                                  updateCartItem(
                                    item.product_id,
                                    "quantity",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_cost}
                                disabled={isReceiptLinked}
                                onChange={(event) =>
                                  updateCartItem(
                                    item.product_id,
                                    "unit_cost",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.discount_amount}
                                onChange={(event) =>
                                  updateCartItem(
                                    item.product_id,
                                    "discount_amount",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.tax_amount}
                                onChange={(event) =>
                                  updateCartItem(
                                    item.product_id,
                                    "tax_amount",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td className="purchase-money">
                              {money(lineTotal)}
                            </td>

                            <td>
                              <button
                                type="button"
                                className="btn-purchase-remove"
                                onClick={() =>
                                  removeCartItem(item.product_id)
                                }
                                disabled={isReceiptLinked}
                              >
                                Quitar
                              </button>
                            </td>
                          </tr>
                          {!isReceiptLinked && (
                            <tr className="purchase-lots-row">
                              <td colSpan="7">
                                <div className="purchase-lots-header">
                                  <div>
                                    <strong>Lotes y vencimientos</strong>
                                    <small>
                                      La suma de los lotes debe coincidir
                                      con la cantidad del producto.
                                    </small>
                                  </div>

                                  <button
                                    type="button"
                                    className="btn-purchase-add-lot"
                                    onClick={() =>
                                      addLotToCartItem(item.product_id)
                                    }
                                    disabled={isReceiptLinked}
                                  >
                                    Agregar lote
                                  </button>
                                </div>

                                <div className="purchase-lots-list">
                                  {item.lots.map((lot, lotIndex) => (
                                    <div
                                      className="purchase-lot-fields"
                                      key={`${item.product_id}-${lotIndex}`}
                                    >
                                      <label>
                                        Número de lote
                                        <input
                                          type="text"
                                          maxLength="100"
                                          value={lot.lot_number}
                                          onChange={(event) =>
                                            updateLot(
                                              item.product_id,
                                              lotIndex,
                                              "lot_number",
                                              event.target.value
                                            )
                                          }
                                          placeholder="Ejemplo: L-2026-001"
                                        />
                                      </label>

                                      <label>
                                        Vencimiento
                                        <input
                                          type="date"
                                          min={purchaseForm.purchase_date}
                                          value={lot.expiration_date}
                                          onChange={(event) =>
                                            updateLot(
                                              item.product_id,
                                              lotIndex,
                                              "expiration_date",
                                              event.target.value
                                            )
                                          }
                                        />
                                      </label>

                                      <label>
                                        Cantidad del lote
                                        <input
                                          type="number"
                                          min="0.001"
                                          step="0.001"
                                          value={lot.quantity}
                                          onChange={(event) =>
                                            updateLot(
                                              item.product_id,
                                              lotIndex,
                                              "quantity",
                                              event.target.value
                                            )
                                          }
                                          required
                                        />
                                      </label>

                                      <button
                                        type="button"
                                        className="btn-purchase-remove-lot"
                                        disabled={item.lots.length === 1}
                                        onClick={() =>
                                          removeLotFromCartItem(
                                            item.product_id,
                                            lotIndex
                                          )
                                        }
                                      >
                                        Quitar lote
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="purchase-totals">
              <span>
                Subtotal:
                <strong>{money(cartTotals.subtotal)}</strong>
              </span>

              <span>
                Descuentos:
                <strong>{money(cartTotals.discount)}</strong>
              </span>

              <span>
                Impuestos:
                <strong>{money(cartTotals.tax)}</strong>
              </span>

              <span className="purchase-total-main">
                Total compra:
                <strong>{money(cartTotals.total)}</strong>
              </span>
            </div>

            <button
              type="submit"
              className="btn-purchase-save"
              disabled={savingPurchase || cart.length === 0}
            >
              {savingPurchase
                ? "Registrando compra..."
                : isReceiptLinked
                  ? "Registrar factura de recepción"
                  : "Registrar compra e ingresar inventario"}
            </button>
          </form>
        ) : (
          <div className="purchase-readonly-message">
            No tienes permiso para registrar compras.
          </div>
        )}

      </section>

      <section className="purchases-card purchase-list-card">
        <div className="purchase-card-heading purchase-list-heading">
          <div>
            <h2>Facturas de proveedor y pagos</h2>
            <p>
              Consulta facturas de proveedor, recepciones vinculadas,
              saldos pendientes y pagos.
            </p>
          </div>

          <div className="purchase-filters">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar proveedor, NIT o factura"
            />

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="partial">Pago parcial</option>
              <option value="paid">Pagadas</option>
              <option value="cancelled">Anuladas</option>
            </select>
          </div>
        </div>

        <div className="purchase-table-wrapper">
          <table className="purchase-history-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Proveedor</th>
                <th>Factura</th>
                <th>Total</th>
                <th>Pagado</th>
                <th>Saldo</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="purchase-empty">
                    Cargando compras...
                  </td>
                </tr>
              ) : purchases.length === 0 ? (
                <tr>
                  <td colSpan="8" className="purchase-empty">
                    No hay compras para mostrar.
                  </td>
                </tr>
              ) : (
                purchases.map((purchase) => (
                  <tr key={purchase.id}>
                    <td>
                      {formatDate(purchase.purchase_date)}
                      {purchase.due_date && (
                        <small>
                          Vence: {formatDate(purchase.due_date)}
                        </small>
                      )}
                    </td>

                    <td>
                      <strong>{purchase.supplier_name}</strong>
                      <small>{purchase.supplier_nit}</small>
                    </td>

                    <td>
                      <strong>
                        {purchase.invoice_number || "Sin referencia"}
                      </strong>

                      {purchase.purchase_order_receipt_id && (
                        <small className="purchase-receipt-badge">
                          Recepción de orden #
                          {purchase.purchase_order_receipt_id}
                        </small>
                      )}
                    </td>

                    <td className="purchase-money">
                      {money(purchase.total)}
                    </td>

                    <td className="purchase-money paid">
                      {money(purchase.paid_amount)}
                    </td>

                    <td className="purchase-money balance">
                      {money(purchase.balance)}
                    </td>

                    <td>
                      <span
                        className={`purchase-status ${purchase.status}`}
                      >
                        {getStatusLabel(purchase.status)}
                      </span>
                    </td>

                    <td className="purchase-actions">
                      {purchase.purchase_order_receipt_id && (
                        <button
                          type="button"
                          className="btn-purchase-receipt"
                          onClick={() =>
                            openReceiptDetail(
                              purchase.purchase_order_receipt_id
                            )
                          }
                        >
                          Ver recepción
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-purchase-payment"
                        onClick={() => openPaymentModal(purchase)}
                        disabled={
                          purchase.status === "paid" ||
                          purchase.status === "cancelled"
                        }
                      >
                        Registrar pago
                      </button>

                      {canViewAccounting && (
                        <button
                          type="button"
                          className="btn-purchase-accounting"
                          onClick={() =>
                            handleViewPurchaseJournalEntry(purchase)
                          }
                        >
                          Asiento
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showPaymentModal && selectedPurchase && (
        <div className="purchase-modal-backdrop">
          <section className="purchase-modal">
            <div className="purchase-modal-header">
              <div>
                <p className="purchases-kicker">Pago a proveedor</p>
                <h2>{selectedPurchase.supplier_name}</h2>
                <span>
                  Compra #{selectedPurchase.id} ·{" "}
                  {selectedPurchase.invoice_number || "Sin factura"}
                </span>
              </div>

              <button
                type="button"
                className="purchase-modal-close"
                onClick={closePaymentModal}
              >
                ×
              </button>
            </div>

            {loadingDetail ? (
              <p className="purchase-empty">
                Cargando detalle de compra...
              </p>
            ) : (
              <>
                <section className="purchase-payment-summary">
                  <article>
                    <span>Total compra</span>
                    <strong>{money(purchaseDetail?.total)}</strong>
                  </article>

                  <article>
                    <span>Pagado</span>
                    <strong className="paid">
                      {money(purchaseDetail?.paid_amount)}
                    </strong>
                  </article>

                  <article>
                    <span>Saldo pendiente</span>
                    <strong className="balance">
                      {money(purchaseDetail?.balance)}
                    </strong>
                  </article>
                </section>

                {purchaseDetail?.status !== "paid" && (
                  canCreatePurchases ? (
                    <form
                      className="purchase-payment-form"
                      onSubmit={handlePaymentSubmit}
                    >
                      <h3>Registrar pago</h3>

                      <div className="purchase-form-grid">
                        <label>
                          Fecha de pago
                          <input
                            type="date"
                            name="payment_date"
                            value={paymentForm.payment_date}
                            onChange={handlePaymentChange}
                            required
                          />
                        </label>

                        <label>
                          Método de pago
                          <select
                            name="payment_method"
                            value={paymentForm.payment_method}
                            onChange={handlePaymentChange}
                          >
                            <option value="transfer">Transferencia</option>
                            <option value="cash">Efectivo</option>
                            <option value="card">Tarjeta</option>
                            <option value="credit">Crédito</option>
                            <option value="other">Otro</option>
                          </select>
                        </label>

                        <label>
                          Valor a pagar
                          <input
                            type="number"
                            min="0.01"
                            max={Number(purchaseDetail?.balance || 0)}
                            step="0.01"
                            name="amount"
                            value={paymentForm.amount}
                            onChange={handlePaymentChange}
                            required
                          />
                        </label>
                      </div>

                      <label>
                        Referencia
                        <input
                          type="text"
                          name="reference"
                          value={paymentForm.reference}
                          onChange={handlePaymentChange}
                          placeholder="Transferencia, recibo o comprobante"
                        />
                      </label>

                      <label>
                        Observación
                        <textarea
                          name="notes"
                          value={paymentForm.notes}
                          onChange={handlePaymentChange}
                          rows="2"
                          placeholder="Información adicional del pago"
                        />
                      </label>

                      {paymentForm.payment_method === "cash" && (
                        <p className="purchase-cash-note">
                          Este pago generará un egreso en Caja. La caja debe
                          estar abierta.
                        </p>
                      )}

                      <button
                        type="submit"
                        className="btn-purchase-save"
                        disabled={savingPayment}
                      >
                        {savingPayment
                          ? "Registrando pago..."
                          : "Registrar pago"}
                      </button>
                    </form>
                  ) : (
                    <div className="purchase-readonly-message">
                      No tienes permiso para registrar pagos.
                    </div>
                  )
                )}

                <section className="purchase-payments-history">
                  <h3>Historial de pagos</h3>

                  {!purchaseDetail?.payments?.length ? (
                    <p className="purchase-empty">
                      No hay pagos registrados.
                    </p>
                  ) : (
                    purchaseDetail.payments.map((payment) => (
                      <article
                        className="purchase-payment-item"
                        key={payment.id}
                      >
                        <div>
                          <strong>
                            {money(payment.amount)} ·{" "}
                            {payment.payment_method}
                          </strong>
                          <span>
                            {formatDate(payment.payment_date)}
                          </span>

                          {payment.reference && (
                            <small>
                              Referencia: {payment.reference}
                            </small>
                          )}
                        </div>
                      </article>
                    ))
                  )}
                </section>
              </>
            )}
          </section>
        </div>
      )}
      {(receiptDetail || loadingReceiptDetail) && (
        <div
          className="purchase-modal-backdrop"
          onClick={() => {
            setReceiptDetail(null);
            setLoadingReceiptDetail(false);
          }}
        >
          <section
            className="purchase-modal purchase-receipt-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="purchase-modal-header">
              <div>
                <p className="purchases-kicker">
                  Recepción de orden
                </p>

                <h2>
                  {receiptDetail?.receipt_number ||
                    "Cargando recepción..."}
                </h2>

                {receiptDetail && (
                  <span>
                    Orden {receiptDetail.order_number} ·{" "}
                    {receiptDetail.supplier_name}
                  </span>
                )}
              </div>

              <button
                type="button"
                className="purchase-modal-close"
                onClick={() => {
                  setReceiptDetail(null);
                  setLoadingReceiptDetail(false);
                }}
              >
                ×
              </button>
            </div>

            {loadingReceiptDetail ? (
              <p className="purchase-empty">
                Cargando detalle de recepción...
              </p>
            ) : (
              <>
                <p className="purchase-receipt-date">
                  Fecha de recepción:{" "}
                  <strong>
                    {formatDate(
                      receiptDetail?.received_at
                    )}
                  </strong>
                </p>

                {receiptDetail?.notes && (
                  <p className="purchase-receipt-notes">
                    <strong>Observaciones:</strong>{" "}
                    {receiptDetail.notes}
                  </p>
                )}

                <div className="purchase-table-wrapper">
                  <table className="purchase-history-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Cantidad</th>
                        <th>Costo unitario</th>
                        <th>Lote</th>
                        <th>Vencimiento</th>
                      </tr>
                    </thead>

                    <tbody>
                      {receiptDetail?.items?.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.product_name}</strong>
                            <small>{item.product_sku}</small>
                          </td>

                          <td>{item.quantity}</td>

                          <td className="purchase-money">
                            {money(item.unit_cost)}
                          </td>

                          <td>
                            {item.lot_number || "Sin lote"}
                          </td>

                          <td>
                            {item.expiration_date
                              ? formatDate(
                                item.expiration_date
                              )
                              : "Sin vencimiento"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      )}
      {linkedJournalEntry && (
        <div
          className="purchase-linked-entry-backdrop"
          onClick={() => setLinkedJournalEntry(null)}
        >
          <section
            className="purchase-linked-entry-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Comprobante contable asociado</h3>

            <p>
              <strong>Número:</strong>{" "}
              {linkedJournalEntry.number}
            </p>

            <p>
              <strong>Fecha:</strong>{" "}
              {new Date(
                `${linkedJournalEntry.entry_date}T00:00:00`
              ).toLocaleDateString("es-CO")}
            </p>

            <p>
              <strong>Descripción:</strong>{" "}
              {linkedJournalEntry.description}
            </p>

            <p>
              <strong>Estado:</strong>{" "}
              {linkedJournalEntry.status}
            </p>

            <div className="purchase-linked-entry-actions">
              {canViewAudit && (
                <button
                  type="button"
                  className="purchase-action-btn purchase-action-accounting"
                  onClick={() =>
                    setAuditJournalEntry(linkedJournalEntry)
                  }
                >
                  Auditoría
                </button>
              )}
              <button
                type="button"
                className="btn-purchase-accounting"
                onClick={() =>
                  navigate(`/${company}/contabilidad`)
                }
              >
                Abrir Contabilidad
              </button>

              <button
                type="button"
                className="btn-purchase-payment"
                onClick={() => setLinkedJournalEntry(null)}
              >
                Cerrar
              </button>
            </div>
          </section>
        </div>
      )}
      {auditJournalEntry && (
        <JournalAuditModal
          journalEntry={auditJournalEntry}
          onClose={() => setAuditJournalEntry(null)}
        />
      )}
    </div>
  );
}

export default CompanyPurchases;